import React, { useState, useEffect } from 'react';
import {
  useStripe,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { analyticsTrack } from '../utils/utils';
import ErrorLabel from './blocks/ErrorLabel';
import InfoText from './InfoText';

/**
 * PaymentRequestButton component for Apple Pay / Google Pay
 * Automatically falls back to card form if wallet payment is not available
 *
 * @param {Object} props
 * @param {number} props.amount - Amount in cents (EUR)
 * @param {string} props.currency - Currency code (default: 'eur')
 * @param {Object} props.client - Client object with prenom, nom, id
 * @param {string} props.clientSecret - Stripe PaymentIntent or SetupIntent client_secret
 * @param {Function} props.onSuccess - Callback on successful payment
 * @param {Function} props.onError - Callback on error
 * @param {boolean} props.isSetupIntent - If true, uses SetupIntent flow (no amount)
 * @param {string} props.label - Payment label (default: 'Roulezjeunesse')
 */
const PaymentRequestButton = ({
  amount,
  currency = 'eur',
  client,
  clientSecret,
  onSuccess,
  onError,
  isSetupIntent = false,
  label = 'Roulezjeunesse',
}) => {
  const stripe = useStripe();
  const { t } = useTranslation();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(null); // null = checking, false = not available, true = available
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  const [walletType, setWalletType] = useState(null);

  useEffect(() => {
    if (!stripe || !clientSecret) {
      return;
    }

    // Create PaymentRequest object
    // IMPORTANT: Apple Pay et Google Pay nécessitent un montant > 0 pour s'afficher
    // Pour SetupIntent, on met 1 centime symbolique (qui ne sera JAMAIS débité)
    // C'est une limitation technique des wallets, pas un vrai paiement
    const displayAmount = isSetupIntent ? 1 : amount || 1; // Minimum 1 centime

    const pr = stripe.paymentRequest({
      country: 'FR',
      currency: currency.toLowerCase(),
      total: {
        label: isSetupIntent ? 'Enregistrement carte - 0 €' : label,
        amount: displayAmount,
      },
      requestPayerName: true,
      requestPayerEmail: false,
    });

    // Register event listeners BEFORE checking availability
    // This is crucial to avoid the "You must register an event listener" error

    // Handle payment method event
    pr.on('paymentmethod', async (ev) => {
      console.log('🔵 paymentmethod event:', ev);
      console.log('🔵 paymentMethod.id:', ev.paymentMethod.id);
      console.log('🔵 isSetupIntent:', isSetupIntent);
      console.log('🔵 clientSecret:', clientSecret);

      setLoading(true);

      try {
        let confirmResult;

        if (isSetupIntent) {
          console.log('🔵 Calling confirmSetup for wallet...');
          console.log('🔵 clientSecret:', clientSecret);
          console.log('🔵 payment_method:', ev.paymentMethod.id);

          // SetupIntent flow - Use confirmSetup for wallets (not confirmCardSetup)
          // Add timeout to prevent indefinite hanging
          const confirmPromise = stripe.confirmSetup({
            clientSecret,
            confirmParams: {
              payment_method: ev.paymentMethod.id,
            },
            redirect: 'if_required',
          });

          // Timeout after 30 seconds
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(
              () =>
                reject(
                  new Error(
                    'Timeout: La confirmation a pris trop de temps. Veuillez réessayer.',
                  ),
                ),
              30000,
            ),
          );

          confirmResult = await Promise.race([confirmPromise, timeoutPromise]);
          console.log('🔵 confirmSetup result:', confirmResult);

          analyticsTrack('wallet_setup_intent_confirmed', {
            setup_intent_id: confirmResult.setupIntent?.id,
            payment_method_id: ev.paymentMethod.id,
            wallet_type: ev.walletName,
            client_id: client?.id,
          });
        } else {
          // PaymentIntent flow (Option A - pre-authorization or immediate payment)
          confirmResult = await stripe.confirmCardPayment(clientSecret, {
            payment_method: ev.paymentMethod.id,
          });

          analyticsTrack('wallet_payment_intent_confirmed', {
            payment_method_id: ev.paymentMethod.id,
            wallet_type: ev.walletName,
            amount,
            currency,
            client_id: client?.id,
          });
        }

        if (confirmResult.error) {
          // Payment failed
          console.log('❌ confirmResult.error:', confirmResult.error);
          ev.complete('fail');
          setError(confirmResult.error.message);
          setLoading(false);

          analyticsTrack('wallet_payment_failed', {
            error_code: confirmResult.error.code,
            error_message: confirmResult.error.message,
            wallet_type: ev.walletName,
            client_id: client?.id,
          });

          if (onError) {
            onError(confirmResult.error);
          }
        } else {
          // Payment succeeded
          console.log('✅ Payment succeeded!');
          console.log('✅ confirmResult:', confirmResult);
          ev.complete('success');

          analyticsTrack('wallet_payment_success', {
            payment_intent_id:
              confirmResult.paymentIntent?.id || confirmResult.setupIntent?.id,
            wallet_type: ev.walletName,
            amount,
            currency,
            client_id: client?.id,
          });

          console.log(
            '✅ Calling onSuccess with:',
            isSetupIntent
              ? confirmResult.setupIntent
              : confirmResult.paymentIntent,
          );

          if (onSuccess) {
            // Pour SetupIntent, passer le payment_method au backend
            if (isSetupIntent) {
              onSuccess({
                setupIntent: confirmResult.setupIntent,
                payment_method:
                  confirmResult.setupIntent?.payment_method ||
                  ev.paymentMethod.id,
              });
            } else {
              onSuccess(confirmResult.paymentIntent);
            }
          }
        }
      } catch (err) {
        console.error('❌ Exception during wallet payment:', err);
        console.error('❌ Error name:', err.name);
        console.error('❌ Error message:', err.message);
        console.error('❌ Error stack:', err.stack);

        ev.complete('fail');

        // Provide user-friendly error messages
        let userMessage = err.message;
        if (
          err.message.includes('network') ||
          err.message.includes('connexion') ||
          err.message.includes('Timeout')
        ) {
          userMessage =
            'Erreur de connexion. Vérifiez votre connexion internet et réessayez.';
        } else if (err.message.includes('session')) {
          userMessage =
            'Session expirée. Veuillez recharger la page et réessayer.';
        }

        setError(userMessage);
        setLoading(false);

        analyticsTrack('wallet_payment_exception', {
          error_message: err.message,
          error_name: err.name,
          wallet_type: ev.walletName,
          client_id: client?.id,
          is_timeout: err.message.includes('Timeout'),
          is_network:
            err.message.includes('network') ||
            err.message.includes('connexion'),
        });

        if (onError) {
          onError(err);
        }
      }
    });

    // Handle cancel event
    pr.on('cancel', () => {
      console.log('❌ Payment Request cancelled');
      setLoading(false);

      analyticsTrack('wallet_payment_cancelled', {
        amount,
        currency,
        client_id: client?.id,
      });
    });

    // Check if Apple Pay or Google Pay is available AFTER registering listeners
    pr.canMakePayment().then((result) => {
      console.log('========================================');
      console.log('🔍 PAYMENT REQUEST DEBUG INFO');
      console.log('========================================');
      console.log('🍎 canMakePayment result:', result);
      console.log('🍎 User Agent:', navigator.userAgent);
      console.log('🍎 Protocol:', window.location.protocol);
      console.log('🍎 Is HTTPS:', window.location.protocol === 'https:');
      console.log('🍎 Hostname:', window.location.hostname);
      console.log('🍎 Full URL:', window.location.href);
      console.log('🍎 Has ApplePaySession:', 'ApplePaySession' in window);
      console.log('🍎 PaymentRequest API:', 'PaymentRequest' in window);
      console.log('🍎 Amount:', displayAmount, 'cents');
      console.log('🍎 Currency:', currency);
      console.log('🍎 Country:', 'FR');
      console.log(
        '🍎 Label:',
        isSetupIntent ? 'Enregistrement carte - 0 €' : label,
      );
      console.log('🍎 isSetupIntent:', isSetupIntent);
      console.log('🍎 clientSecret:', clientSecret ? 'Present' : 'Missing');

      if (result) {
        console.log('✅ WALLET AVAILABLE!');
        console.log('✅ Details:', JSON.stringify(result, null, 2));
        console.log('✅ applePay:', result.applePay);
        console.log('✅ googlePay:', result.googlePay);
        console.log('✅ link:', result.link);

        // IMPORTANT: On affiche le bouton même si seul Link est disponible
        // Stripe affichera automatiquement le bon wallet (Apple Pay, Google Pay ou Link)
        setPaymentRequest(pr);
        setCanMakePayment(true);
        setDebugInfo(
          `✅ ${
            result.applePay
              ? 'Apple Pay'
              : result.googlePay
              ? 'Google Pay'
              : result.link
              ? 'Link'
              : 'Wallet'
          } disponible`,
        );

        console.log(
          '✅ Payment Request available:',
          result.applePay
            ? 'Apple Pay'
            : result.googlePay
            ? 'Google Pay'
            : result.link
            ? 'Link'
            : 'Unknown',
        );

        // Track availability
        analyticsTrack('payment_request_button_available', {
          wallet_type: result.applePay
            ? 'apple_pay'
            : result.googlePay
            ? 'google_pay'
            : result.link
            ? 'link'
            : 'unknown',
          amount,
          currency,
          client_id: client?.id,
        });
      } else {
        console.log('❌ NO WALLET AVAILABLE');
        console.log('❌ Result is:', result);

        setCanMakePayment(false);
        setDebugInfo(
          `❌ Wallet non disponible. HTTPS: ${
            window.location.protocol === 'https:'
          }, Safari: ${
            /Safari/.test(navigator.userAgent) &&
            !/Chrome/.test(navigator.userAgent)
          }`,
        );

        console.log('❌ Payment Request NOT available');
        console.log('❌ Possible reasons:');
        console.log('   - No card in Wallet (iPhone)');
        console.log('   - Not Safari (iPhone)');
        console.log('   - Not HTTPS');
        console.log('   - Touch ID not configured (Mac)');
        console.log('   - Invalid SSL certificate');
        console.log('   - Cloudflare Tunnel SSL issue');

        // Track unavailability
        analyticsTrack('payment_request_button_unavailable', {
          amount,
          currency,
          client_id: client?.id,
          user_agent: navigator.userAgent,
          protocol: window.location.protocol,
          hostname: window.location.hostname,
        });
      }
      console.log('========================================');
    });

    return () => {
      // Cleanup
      pr.off('paymentmethod');
      pr.off('click');
      pr.off('cancel');
    };
  }, [
    stripe,
    clientSecret,
    amount,
    currency,
    client,
    isSetupIntent,
    label,
    onSuccess,
    onError,
  ]);

  // Show wallet button only if available
  if (canMakePayment === null) {
    // Still checking availability
    return null;
  }

  if (canMakePayment === false) {
    // Not available - don't show anything
    return null;
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      {paymentRequest && (
        <>
          <PaymentRequestButtonElement
            key={clientSecret} // Force remount when clientSecret changes
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  type: 'default',
                  theme: 'dark',
                  height: '44px',
                },
              },
            }}
          />
          {/* Info sécurité */}
          <div
            style={{
              marginTop: '12px',
              padding: '12px 16px',
              background: '#f8f9fa',
              borderRadius: '8px',
              borderLeft: '3px solid #5b5ff5',
            }}>
            <div
              style={{
                fontFamily: 'LTSaeada, Arial, sans-serif',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#2e414b',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
              💡 Info sécurité
            </div>
            <div
              style={{
                fontFamily: 'LTSaeada, Arial, sans-serif',
                fontSize: '0.8rem',
                color: 'rgba(46, 65, 75, 0.8)',
                lineHeight: 1.5,
              }}>
              Pour vérifier votre moyen de paiement, Google Pay ou Apple Pay
              peuvent afficher une empreinte temporaire (0,01 €). Ce n'est pas
              un paiement, simplement une vérification automatique. Elle ne sera
              pas débité.
            </div>
          </div>
        </>
      )}

      {error && <ErrorLabel>{error}</ErrorLabel>}

      {loading && (
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <InfoText>{t('processing', 'Traitement en cours...')}</InfoText>
        </div>
      )}

      <div
        style={{
          textAlign: 'center',
          margin: '12px 0',
          color: 'rgba(46, 65, 75, 0.4)',
          fontWeight: 600,
          fontFamily: 'LTSaeada, Arial, sans-serif',
          fontSize: '0.85rem',
        }}>
        <span>{t('or', 'ou')}</span>
      </div>
    </div>
  );
};

export default PaymentRequestButton;
